// Currency mapping for ISO-3 codes to symbols and formatting
export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  decimals: number;
  symbolPosition: 'before' | 'after';
}

// Comprehensive currency mapping for world currencies
export const CURRENCIES: Record<string, CurrencyInfo> = {
  // Major World Currencies
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', decimals: 2, symbolPosition: 'before' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', decimals: 2, symbolPosition: 'before' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', decimals: 2, symbolPosition: 'before' },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen', decimals: 0, symbolPosition: 'before' },
  CNY: { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', decimals: 2, symbolPosition: 'before' },
  
  // African Currencies
  TND: { code: 'TND', symbol: 'د.ت', name: 'Tunisian Dinar', decimals: 3, symbolPosition: 'after' },
  MAD: { code: 'MAD', symbol: 'د.م.', name: 'Moroccan Dirham', decimals: 2, symbolPosition: 'after' },
  EGP: { code: 'EGP', symbol: 'ج.م', name: 'Egyptian Pound', decimals: 2, symbolPosition: 'after' },
  ZAR: { code: 'ZAR', symbol: 'R', name: 'South African Rand', decimals: 2, symbolPosition: 'before' },
  NGN: { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', decimals: 2, symbolPosition: 'before' },
  
  // Middle Eastern Currencies
  AED: { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', decimals: 2, symbolPosition: 'after' },
  SAR: { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal', decimals: 2, symbolPosition: 'after' },
  QAR: { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal', decimals: 2, symbolPosition: 'after' },
  KWD: { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar', decimals: 3, symbolPosition: 'after' },
  
  // Asian Currencies
  INR: { code: 'INR', symbol: '₹', name: 'Indian Rupee', decimals: 2, symbolPosition: 'before' },
  SGD: { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', decimals: 2, symbolPosition: 'before' },
  HKD: { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar', decimals: 2, symbolPosition: 'before' },
  KRW: { code: 'KRW', symbol: '₩', name: 'South Korean Won', decimals: 0, symbolPosition: 'before' },
  THB: { code: 'THB', symbol: '฿', name: 'Thai Baht', decimals: 2, symbolPosition: 'before' },
  
  // American Currencies
  CAD: { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', decimals: 2, symbolPosition: 'before' },
  MXN: { code: 'MXN', symbol: '$', name: 'Mexican Peso', decimals: 2, symbolPosition: 'before' },
  BRL: { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', decimals: 2, symbolPosition: 'before' },
  ARS: { code: 'ARS', symbol: '$', name: 'Argentine Peso', decimals: 2, symbolPosition: 'before' },
  
  // European Currencies
  CHF: { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', decimals: 2, symbolPosition: 'before' },
  SEK: { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', decimals: 2, symbolPosition: 'after' },
  NOK: { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', decimals: 2, symbolPosition: 'after' },
  DKK: { code: 'DKK', symbol: 'kr', name: 'Danish Krone', decimals: 2, symbolPosition: 'after' },
  PLN: { code: 'PLN', symbol: 'zł', name: 'Polish Zloty', decimals: 2, symbolPosition: 'after' },
  
  // Oceanian Currencies
  AUD: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', decimals: 2, symbolPosition: 'before' },
  NZD: { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', decimals: 2, symbolPosition: 'before' },
  
  // Cryptocurrency (for modern POS systems)
  BTC: { code: 'BTC', symbol: '₿', name: 'Bitcoin', decimals: 8, symbolPosition: 'before' },
  ETH: { code: 'ETH', symbol: 'Ξ', name: 'Ethereum', decimals: 6, symbolPosition: 'before' }
};

// Format currency amount with proper symbol and positioning
export const formatCurrency = (amount: number, currencyCode: string): string => {
  const currency = CURRENCIES[currencyCode.toUpperCase()];
  
  if (!currency) {
    // Fallback for unknown currencies
    return `${amount.toFixed(2)} ${currencyCode}`;
  }
  
  const formattedAmount = amount.toFixed(currency.decimals);
  
  if (currency.symbolPosition === 'before') {
    return `${currency.symbol}${formattedAmount}`;
  } else {
    return `${formattedAmount} ${currency.symbol}`;
  }
};

// Get currency symbol only
export const getCurrencySymbol = (currencyCode: string): string => {
  const currency = CURRENCIES[currencyCode.toUpperCase()];
  return currency ? currency.symbol : currencyCode;
};

// Get currency info
export const getCurrencyInfo = (currencyCode: string): CurrencyInfo | null => {
  return CURRENCIES[currencyCode.toUpperCase()] || null;
};

// Validate currency code
export const isValidCurrency = (currencyCode: string): boolean => {
  return currencyCode.toUpperCase() in CURRENCIES;
};

// Get all supported currencies
export const getSupportedCurrencies = (): CurrencyInfo[] => {
  return Object.values(CURRENCIES);
}; 